<template>
    <v-container class="pa-5" fluid>
        <AssignComponentDialog
            :is-open="assignComponentDialog"
            :product-components="productComponents"
            @on-save="addProductComponent"
            @on-close="closeAssignComponentDialog"
        />

        <AssignProductionPropertiesDialog
            :is-open="assignProductionPropertiesDialog"
            :component="currentProductionComponent"
            :properties="productProductionProperties"
            @on-save="saveProductProductionProperties"
            @on-close="closeAssignProductionPropertiesDialog"
        />

        <AssignProductionProcessDialog
            :is-open="assignProductionProcessDialog"
            :component="currentProductionComponent"
            :production-processes="productionProcesses"
            @on-save="saveProductionProcess"
            @on-close="closeProductionProcessDialog"
        />

        <ConfirmDialog
            :is-open="confirmRemoveComponentDialog"
            :data="currentProductionComponentIndex"
            @on-confirm="removeProductComponent(currentProductionComponentIndex)"
            @on-abort="closeConfirmRemoveComponentDialog"
        >
            <template #title>
                Usunąć komponent z produktu?
            </template>
            <template #content>
                Czy na pewno chcesz usunąć komponent?
            </template>
        </ConfirmDialog>

        <AssignProductTemplateDialog
            :is-open="assignProductTemplateDialog"
            @on-save="addProductTemplate"
            @on-close="closeAssignProductTemplateDialog"
        />

        <page-title>{{ pageTitle }}</page-title>

        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Parametry produktu
                            </h2>
                        </v-col>
                        <v-col v-if="actionName === 'addProduct'" cols="auto">
                            <v-btn
                                color="primary"
                                text
                                data-sentry-label="Wybierz z szablonu"
                                @click="showAssignProductTemplateDialog"
                            >
                                Wybierz z szablonu
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-container>
                                    <v-row align="center">
                                        <v-col cols="12" md="4">
                                            <v-text-field
                                                v-model="productTemplate.name"
                                                label="Nazwa produktu"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productTemplateNameErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-autocomplete
                                                v-model="productTemplate.jsonTemplate.productTypeId"
                                                :items="products"
                                                label="Rodzaj produktu"
                                                item-text="name"
                                                item-value="id"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productTemplateProductTypeErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="productTemplate.jsonTemplate.formatA"
                                                label="Szerokość"
                                                suffix="mm"
                                                outlined
                                                dense
                                                :error-messages="productTemplateFormatAErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="productTemplate.jsonTemplate.formatB"
                                                label="Wysokość"
                                                suffix="mm"
                                                outlined
                                                dense
                                                :error-messages="productTemplateFormatBErrors()"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="product.amount"
                                                v-mask="'#######'"
                                                label="Nakład"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productAmountErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="product.numberOfVersions"
                                                label="Ilość wzorów"
                                                disabled
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" md="8">
                                            <v-autocomplete
                                                v-model="product.screenGroup"
                                                :items="screenGroups"
                                                :filter="customFilter"
                                                :loading="screenGroupSearchLoading"
                                                :search-input.sync="screenGroupSearch"
                                                class="v-text-field--placeholder-dark"
                                                label="Grupy ekranów"
                                                multiple
                                                chips
                                                dense
                                                outlined
                                                deletable-chips
                                                return-object
                                                cache-items
                                                required
                                                :error-messages="productScreenGroupErrors()"
                                                item-value="id"
                                                no-data-text="Nie znaleziono"
                                                @change="clearScreenGroupsSearch()"
                                            >
                                                <template slot="no-data">
                                                    <v-list-item>
                                                        <v-list-item-title>
                                                            <template v-if="!screenGroupSearchLoading">
                                                                Wpisz treść aby odnaleźć <strong>grupę</strong>.
                                                            </template>
                                                            <template v-else-if="screenGroupSearchLoading">
                                                                Trwa wyszukiwanie...
                                                            </template>
                                                            <template v-else>
                                                                Nie znaleziono
                                                            </template>
                                                        </v-list-item-title>
                                                    </v-list-item>
                                                </template>
                                                <template slot="selection" slot-scope="data">
                                                    <v-chip
                                                        small
                                                        close
                                                        @update:active="removeScreenGroups(data.item)"
                                                    >
                                                        {{ data.item.name }}
                                                    </v-chip>
                                                </template>

                                                <template slot="item" slot-scope="data">
                                                    {{ data.item.name }}
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-textarea
                                                v-model="productTemplate.jsonTemplate.description"
                                                label="Uwagi"
                                                outlined
                                                hide-details
                                            />
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Pliki produkcyjne
                            </h2>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <v-card :loading="actionLoading.fileRemoving" loader-height="3">
                                <v-overlay
                                    color="white"
                                    opacity="0.85"
                                    absolute
                                    :value="actionLoading.fileUploading"
                                >
                                    <v-container class="pa-0">
                                        <v-row justify="center">
                                            <v-col cols="auto">
                                                <v-progress-circular
                                                    :rotate="-90"
                                                    :size="120"
                                                    :width="4"
                                                    :value="uploadingProgressValue"
                                                    color="primary"
                                                >
                                                    {{ uploadingProgressValue }}
                                                </v-progress-circular>
                                            </v-col>
                                        </v-row>
                                        <v-row justify="center">
                                            <v-col cols="auto">
                                                <v-btn
                                                    color="error"
                                                    small
                                                    outlined
                                                    data-sentry-label="Anuluj"
                                                    @click="abortUploadingProductionFileAttachment"
                                                >
                                                    Anuluj
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-overlay>
                                <v-card-text>
                                    <v-container class="pa-0">
                                        <v-row>
                                            <v-col cols="auto" sm="8" md="6" lg="8">
                                                <v-file-input
                                                    v-model="currentProductionFile"
                                                    label="Plik produkcyjny"
                                                    counter
                                                    placeholder="Wybierz plik"
                                                    prepend-icon="mdi-paperclip"
                                                    outlined
                                                    dense
                                                    :show-size="1000"
                                                    :error-messages="currentProductionFileErrors()"
                                                >
                                                    <template #selection="{index, text}">
                                                        <v-chip
                                                            v-if="index < 2"
                                                            label
                                                            small
                                                        >
                                                            {{ text }}
                                                        </v-chip>
                                                    </template>
                                                </v-file-input>
                                            </v-col>
                                            <v-col cols="auto">
                                                <v-btn
                                                    color="primary"
                                                    outlined
                                                    data-sentry-label="Wyślij plik"
                                                    :disabled="!currentProductionFile"
                                                    @click="addProductionFileAttachment"
                                                >
                                                    <v-icon left>
                                                        mdi-file-upload
                                                    </v-icon>
                                                    Wyślij
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </v-container>

                                    <v-data-table
                                        v-if="product.productionFiles.length > 0"
                                        :items="product.productionFiles"
                                        :headers="headers"
                                        disable-pagination
                                        hide-default-footer
                                        :mobile-breakpoint="0"
                                    >
                                        <template #item.fileName="{item}">
                                            {{ productionFileName(item.file) }}
                                        </template>
                                        <template #item.actions="{item}">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń załącznik"
                                                            v-on="on"
                                                            @click="deleteProductionFileAttachment(item.id)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń</span>
                                                </v-tooltip>
                                            </div>
                                        </template>
                                    </v-data-table>

                                    <v-alert
                                        v-else
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Nie dodano pliku produkcyjnego.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                color="error"
                                medium
                                class="mr-3"
                                data-sentry-label="Wstecz"
                                :disabled="actionLoading.fileUploading || actionLoading.fileRemoving"
                                @click="close"
                            >
                                Wstecz
                            </v-btn>
                            <v-btn
                                color="success"
                                medium
                                data-sentry-label="Zapisz"
                                :disabled="actionLoading.fileUploading || actionLoading.fileRemoving"
                                @click="saveProduct"
                            >
                                Zapisz
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Komponenty
                            </h2>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn
                                color="secondary"
                                fab
                                small
                                class="elevation-1"
                                data-sentry-label="Dodaj komponent"
                                @click="showAssignComponentDialog"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col v-if="productTemplate.jsonTemplate.campaignProductComponents.length === 0" cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Produkt nie posiada przypisanych komponentów.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>

                        <v-col v-for="(component, componentIndex) in productTemplate.jsonTemplate.campaignProductComponents" v-else :key="component.id" cols="12">
                            <v-card>
                                <v-card-title>
                                    <v-row align="center">
                                        <v-col>
                                            <div class="text-h6 text-no-wrap">
                                                {{ component.component.name }} ({{ component.component.code }})
                                            </div>
                                        </v-col>
                                        <v-col sm="auto">
                                            <v-btn
                                                color="primary"
                                                medium
                                                text
                                                class="mx-1"
                                                data-sentry-label="Dodaj cechy produkcyjne"
                                                @click="showAssignProductionPropertiesDialog(component, componentIndex)"
                                            >
                                                Dodaj cechy produkcyjne
                                            </v-btn>
                                            <v-btn
                                                color="primary"
                                                medium
                                                text
                                                class="mx-1"
                                                data-sentry-label="Dodaj procesy produkcyjne"
                                                @click="showAssignProductionProcessDialog(component, componentIndex)"
                                            >
                                                Dodaj procesy produkcyjne
                                            </v-btn>
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Usuń komponent"
                                                        v-on="on"
                                                        @click="showConfirmRemoveComponentDialog(componentIndex)"
                                                    >
                                                        <v-icon color="pink">
                                                            mdi-delete
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Usuń komponent</span>
                                            </v-tooltip>
                                        </v-col>
                                    </v-row>
                                </v-card-title>

                                <v-card-text>
                                    <v-alert
                                        v-if="component.campaignComponentProperties.length === 0"
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Brak przypisanych cech produkcyjnych.
                                    </v-alert>
                                    <v-row v-else>
                                        <v-col cols="12">
                                            <strong>Cechy produkcyjne:</strong>
                                        </v-col>
                                        <v-col
                                            v-for="(productionProperty, propertyIndex) in component.campaignComponentProperties"
                                            :key="productionProperty.productProductionProperty.id"
                                            cols="12"
                                            sm="4"
                                            lg="3"
                                        >
                                            <div class="d-inline-flex">
                                                <v-autocomplete
                                                    v-if="productionProperty.productProductionProperty.name === 'rodzaj_papieru'"
                                                    v-model="productionProperty.value"
                                                    :items="papers"
                                                    :filter="customFilter"
                                                    :label="productionProperty.productProductionProperty.verboseName + ':'"
                                                    placeholder="wartość"
                                                    item-value="name"
                                                    item-text="name"
                                                    no-data-text="Nie znaleziono"
                                                    :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                    outlined
                                                    dense
                                                    required
                                                />
                                                <v-text-field
                                                    v-else
                                                    v-model="productionProperty.value"
                                                    :label="productionProperty.productProductionProperty.verboseName + ':'"
                                                    placeholder="wartość"
                                                    :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                    outlined
                                                    dense
                                                    required
                                                />
                                                <v-btn
                                                    icon
                                                    class="mt-1"
                                                    data-sentry-label="Usuń cechę produkcyjną"
                                                    @click="removeProductProductionProperty(componentIndex, productionProperty)"
                                                >
                                                    <v-icon>
                                                        mdi-close
                                                    </v-icon>
                                                </v-btn>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                                <v-divider />
                                <v-card-text>
                                    <v-alert
                                        v-if="component.campaignComponentProcesses.length === 0"
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Brak przypisanych procesów produkcyjnych.
                                    </v-alert>
                                    <v-row v-else>
                                        <v-col cols="12">
                                            <strong>Procesy produkcyjne:</strong>
                                        </v-col>
                                        <v-col
                                            v-for="(productionProcess, processIndex) in component.campaignComponentProcesses"
                                            :key="productionProcess.productionProcess.id"
                                            cols="12"
                                            sm="4"
                                            lg="3"
                                        >
                                            <div class="d-inline-flex">
                                                <v-text-field
                                                    v-model="productionProcess.order"
                                                    :label="productionProcess.productionProcess.name + ':'"
                                                    placeholder="kolejność"
                                                    type="number"
                                                    :error-messages="productionProcessOrderErrors(componentIndex, processIndex)"
                                                    outlined
                                                    dense
                                                    required
                                                />
                                                <v-btn
                                                    icon
                                                    class="mt-1"
                                                    data-sentry-label="Usuń proces produkcyjny"
                                                    @click="removeProductionProcess(componentIndex, productionProcess)"
                                                >
                                                    <v-icon>
                                                        mdi-close
                                                    </v-icon>
                                                </v-btn>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import AssignComponentDialog from '@/views/Configurations/Common/AssignComponentDialog'
import AssignProductionPropertiesDialog from '@/views/Configurations/Common/AssignProductionPropertiesDialog'
import AssignProductionProcessDialog from '@/views/Configurations/Common/AssignProductionProcessDialog'
import AssignProductTemplateDialog from './AssignProductTemplateDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import tooltipDelay from '@/mixins/tooltip'
import customFilters from '@/mixins/customFilters'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { appConfig } from '@/config'
import { required, integer } from 'vuelidate/lib/validators'
import { mask } from 'vue-the-mask'

let searchTimer
const maxFileSize = (value) => {
    return value.size < appConfig.orders.productionFileAttachmentSize * 1024 * 1024
}


export default {
    name: 'Product',
    components: {
        PageTitle,
        AssignComponentDialog,
        AssignProductionPropertiesDialog,
        AssignProductionProcessDialog,
        ConfirmDialog,
        AssignProductTemplateDialog
    },
    directives: { mask },
    mixins: [
        tooltipDelay,
        customFilters
    ],
    beforeRouteEnter(to, from, next) {
        if (from.name) {
            Promise.all([
                store.dispatch('configurations/getProducts', {
                    shouldSavedInStore: false
                }),
                store.dispatch('configurations/getProductProductionProperties', {
                    query: '?orderAvailable=true'
                }),
                store.dispatch('configurations/getProductComponents'),
                store.dispatch('configurations/getProductionProcesses', {paginate: '', query: '?isVirtual=false'}),
                store.dispatch('configurations/getPapers'),
            ])
                .then(responses => {
                    next(vm => {
                        vm.products = responses[0].data.results
                        vm.productProductionProperties = responses[1].data.results
                        vm.productComponents = responses[2].data.results,
                        vm.productionProcesses = responses[3].data.results,
                        vm.papers = responses[4].data.results
                        vm.productTemplateIndex = to.params.index
                        vm.pageTitle = to.meta.title
                        if (typeof to.params.index !== 'undefined') { // Product edit
                            vm.actionName = 'updateProduct'
                            vm.product.amount = store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign.campaignProducts[to.params.index].amount,
                            vm.product.numberOfVersions = store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign.campaignProducts[to.params.index].numberOfVersions,
                            vm.product.weight = store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign.campaignProducts[to.params.index].weight,
                            vm.product.productionFiles = store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign.campaignProducts[to.params.index].productionFiles
                            vm.product.screenGroupIds = store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign.campaignProducts[to.params.index].screenGroupIds
                            vm.product.screenGroup = store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign.campaignProducts[to.params.index].screenGroup
                            vm.screenGroups = store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign.campaignProducts[to.params.index].screenGroup
                            vm.productTemplate = Object.assign({}, store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.productTemplates[to.params.index])
                        } else {
                            vm.actionName = 'addProduct'
                        }
                    })
                })
                .catch(error => {
                    next(error)
                    errorHandler(error)
                })
        } else {
            next({ name: 'outdoorsCampaignsCampaignsOutdoorsCampaignCreate' })
        }
    },
    data() {
        return {
            papers: [],
            actionName: null,
            pageTitle: '',
            product: {
                amount: null,
                numberOfVersions: 1, // Temporary hardcoded
                weight: 0, // Temporary hardcoded
                productionFiles: [],
                screenGroupIds: [],
                screenGroup: []
            },
            productTemplate: {
                name: null,
                jsonTemplate: {
                    productTypeId: null,
                    formatA: null,
                    formatB: null,
                    campaignProductComponents: [],
                    description: null
                }
            },
            currentProductionFile: null,
            currentProductionComponent: {
                component: {
                    name: null,
                    code: null
                },
                campaignComponentProperties: [],
                campaignComponentProcesses: []
            },
            products: [],
            productComponents: [],
            productProductionProperties: [],
            productionProcesses: [],
            productTemplateIndex: null,
            currentProductionComponentIndex: null,
            assignComponentDialog: false,
            confirmRemoveComponentDialog: false,
            assignProductionPropertiesDialog: false,
            hasProductChanges: false,
            assignProductionProcessDialog: false,
            assignProductTemplateDialog: false,
            actionLoading: {
                fileUploading: false,
                fileRemoving: false
            },
            screenGroupSearch: null,
            screenGroupSearchLoading: false,
            screenGroups: [],
            productTemplateName: null,
            headers: [
                {
                    text: 'Nazwa pliku',
                    value: 'fileName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ]
        }
    },
    validations() {
        const rules = {
            product: {
                amount: {
                    required
                },
                screenGroup: {
                    required
                }
            },
            productTemplate: {
                name: {
                    required,
                    isSameAsProductTemplateName: (value) => {
                        if (!value) return true

                        return value.trim() !== this.productTemplateName
                    },
                    alreadyExists: (value) => {
                        if (!value) return true

                        const products = this.temporarySavedCampaign.campaignProducts.map(product => product.name.trim())

                        return !products.includes(value.trim())
                    }
                },
                jsonTemplate: {
                    productTypeId: {
                        required
                    },
                    campaignProductComponents: {
                        $each: {
                            campaignComponentProperties: {
                                $each: {
                                    value: {
                                        required
                                    }
                                }
                            },
                            campaignComponentProcesses: {
                                $each: {
                                    order: {
                                        required
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (this.currentProductionFile) {
            rules['currentProductionFile'] = {
                maxFileSize
            }
        }

        if (this.productTemplate.jsonTemplate.formatA) {
            rules.productTemplate.jsonTemplate['formatA'] = {
                integer
            }
        }

        if (this.productTemplate.jsonTemplate.formatB) {
            rules.productTemplate.jsonTemplate['formatB'] = {
                integer
            }
        }

        return rules
    },
    computed: {
        uploadingProgressValue() {
            return this.$store.state.common.progress.value
        },
        temporarySavedCampaign() {
            return this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign
        }
    },
    watch: {
        screenGroupSearch(value = null) {
            if (value === null) return

            this.screenGroupSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchScreenGroups(value)
                } else {
                    this.screenGroupSearchLoading = false
                    this.screenGroups = []
                }
            }, 500)
        },
    },
    methods: {
        searchScreenGroups(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie grup ekranów')

            this.$store.dispatch('outdoors/outdoorsSettings/getLocalizationScreenGroups', {
                paginate: '?limit=20',
                query: `&name=${query}&customerId=${this.temporarySavedCampaign.customerId}`
            })
                .then(response => {
                    this.screenGroups = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.screenGroupSearchLoading = false
                })
        },
        clearScreenGroupsSearch() {
            this.screenGroupSearch = null
        },
        removeScreenGroups(screenGroup) {
            const index = this.product.screenGroup.findIndex(item => item.id === screenGroup.id)

            this.product.screenGroup.splice(index, 1)
        },
        addProductionFileAttachment() {
            this.$v.currentProductionFile.$touch()

            if (this.$v.currentProductionFile.$invalid) return

            const formData = new FormData()

            formData.append('file', this.currentProductionFile)

            this.actionLoading.fileUploading = true

            this.$store.dispatch('outdoors/outdoorsCampaigns/addProductionFileAttachment', {
                formData: formData,
                abort: false
            })
                .then(response => {
                    this.product.productionFiles.push(response.data)
                    this.currentProductionFile = null
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.fileUploading = false
                })
        },
        abortUploadingProductionFileAttachment() {
            this.$store.dispatch('outdoors/outdoorsCampaigns/addProductionFileAttachment', {
                formData: null,
                abort: true
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        deleteProductionFileAttachment(id) {
            this.actionLoading.fileRemoving = true
            this.$store.dispatch('outdoors/outdoorsCampaigns/deleteProductionFileAttachment', id)
                .then(() => {
                    const index = this.product.productionFiles.findIndex(item => item.id == id)

                    this.product.productionFiles.splice(index, 1)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.fileRemoving = false
                })
        },
        showAssignProductTemplateDialog() {
            store.dispatch('orders/getProductTemplates')
                .then(() => {
                    this.assignProductTemplateDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeAssignProductTemplateDialog() {
            this.assignProductTemplateDialog = false
        },
        showAssignComponentDialog() {
            this.assignComponentDialog = true
        },
        closeAssignComponentDialog() {
            this.assignComponentDialog = false
        },
        addProductComponent(component) {
            this.hasProductChanges = true
            this.productTemplate.jsonTemplate.campaignProductComponents.push({
                component: component,
                componentId: component.id,
                campaignComponentProcesses: [],
                campaignComponentProperties: []
            })
        },
        removeProductComponent(index) {
            this.hasProductChanges = true
            this.productTemplate.jsonTemplate.campaignProductComponents.splice(index, 1)
            this.closeConfirmRemoveComponentDialog()
        },
        showConfirmRemoveComponentDialog(componentIndex) {
            this.confirmRemoveComponentDialog = true
            this.currentProductionComponentIndex = componentIndex
        },
        closeConfirmRemoveComponentDialog() {
            this.confirmRemoveComponentDialog = false
            this.clearCurrentProductionComponent()
        },
        showAssignProductionPropertiesDialog(component, index) {
            this.assignProductionPropertiesDialog = true
            this.currentProductionComponent = component
            this.currentProductionComponentIndex = index
        },
        closeAssignProductionPropertiesDialog() {
            this.assignProductionPropertiesDialog = false
            setTimeout(() => {
                this.clearCurrentProductionComponent()
            }, 500)
        },
        saveProductProductionProperties(productionProperties) {
            productionProperties.forEach(item => {
                this.productTemplate.jsonTemplate.campaignProductComponents[this.currentProductionComponentIndex].campaignComponentProperties.push({
                    productProductionPropertyId: item.id,
                    productProductionProperty: item
                })
            })

            this.hasProductChanges = true
        },
        removeProductProductionProperty(componentIndex, productionProperty) {
            const campaignComponentProperties = this.productTemplate.jsonTemplate.campaignProductComponents[componentIndex].campaignComponentProperties

            campaignComponentProperties.splice(campaignComponentProperties.indexOf(productionProperty), 1)

            this.hasProductChanges = true
        },
        showAssignProductionProcessDialog(component, index) {
            this.assignProductionProcessDialog = true
            this.currentProductionComponent = component
            this.currentProductionComponentIndex = index
        },
        saveProductionProcess(productionProcesses) {
            productionProcesses.forEach(item => {

                this.productTemplate.jsonTemplate.campaignProductComponents[this.currentProductionComponentIndex].campaignComponentProcesses.push({
                    productionProcessId: item.id,
                    productionProcess: item
                })
            })

            this.hasProductChanges = true
        },
        removeProductionProcess(componentIndex, productionProcess) {
            const campaignComponentProcesses = this.productTemplate.jsonTemplate.campaignProductComponents[componentIndex].campaignComponentProcesses

            campaignComponentProcesses.splice(campaignComponentProcesses.indexOf(productionProcess), 1)

            this.hasProductChanges = true
        },
        closeProductionProcessDialog() {
            this.assignProductionProcessDialog = false

            setTimeout(() => {
                this.clearCurrentProductionComponent()
            }, 500)
        },
        addProductTemplate(payload) {
            this.assignProductTemplateDialog = false
            store.dispatch('outdoors/outdoorsCampaigns/getProductTemplate', {
                productTemplateId: payload.productTemplateId,
                query: '?mode=campaign'
            })
                .then(response => {
                    this.productTemplate = Object.assign({}, response.data)
                    this.productTemplateName = this.productTemplate.name
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        saveProduct() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }
            const actionName = this.actionName === 'addProduct' ? 'updateCampaignCreateProductList' : 'updateCampaignCreateProductTemplate'

            this.hasProductChanges = false
            this.$store.dispatch(`outdoors/outdoorsCampaigns/${actionName}`, {
                index: this.productTemplateIndex,
                product: {
                    name: this.productTemplate.name,
                    productTypeId: this.productTemplate.jsonTemplate.productTypeId,
                    amount: this.product.amount,
                    description: this.productTemplate.jsonTemplate.description,
                    numberOfVersions: this.product.numberOfVersions,
                    screenGroup: this.product.screenGroup,
                    screenGroupIds: this.product.screenGroup.map(item => item.id),
                    campaignProductComponents: this.productTemplate.jsonTemplate.campaignProductComponents,
                    weight: this.product.weight,
                    productionFiles: this.product.productionFiles,
                    campaignProductAttachmentIds: this.product.productionFiles.map(item => item.id),
                    formatA: this.productTemplate.jsonTemplate.formatA,
                    formatB: this.productTemplate.jsonTemplate.formatB,
                },
                productTemplate: this.productTemplate
            })
            this.$router.push({
                name: 'outdoorsCampaignsCampaignsOutdoorsCampaignCreate'
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        clearCurrentProductionComponent() {
            this.currentProductionComponent = {
                component: {
                    name: null,
                    code: null
                },
                campaignComponentProperties: [],
                campaignComponentProcesses: []
            }
            this.currentProductionComponentIndex = null
        },
        productionFileName(fileName) {
            const words = fileName.split('/')

            return words[words.length-1]
        },
        close() {
            this.$router.push({ name: 'outdoorsCampaignsCampaignsOutdoorsCampaignCreate' })
        },

        // Walidacje
        productTemplateNameErrors() {
            const errors = []

            if (!this.$v.productTemplate.name.$dirty) return errors
            !this.$v.productTemplate.name.required && errors.push('Proszę podać nazwę produktu.')
            !this.$v.productTemplate.name.isSameAsProductTemplateName && errors.push('Nazwa produktu nie może być taka sama, jak nazwa wybrana z szablonu.')
            !this.$v.productTemplate.name.alreadyExists && errors.push('Produkt o takiej nazwie został już dodany.')

            return errors
        },
        productTemplateProductTypeErrors() {
            const errors = []

            if (!this.$v.productTemplate.jsonTemplate.productTypeId.$dirty) return errors
            !this.$v.productTemplate.jsonTemplate.productTypeId.required && errors.push('Proszę podać rodzaj produktu.')

            return errors
        },
        productTemplateFormatAErrors() {
            if (this.productTemplate.jsonTemplate.formatA) {
                const errors = []

                if (!this.$v.productTemplate.jsonTemplate.formatA.$dirty) return errors
                this.$v.productTemplate.jsonTemplate.formatA.$invalid && errors.push('Nieprawidłowa wartość.')

                return errors
            }
        },
        productTemplateFormatBErrors() {
            if (this.productTemplate.jsonTemplate.formatB) {
                const errors = []

                if (!this.$v.productTemplate.jsonTemplate.formatB.$dirty) return errors
                this.$v.productTemplate.jsonTemplate.formatB.$invalid && errors.push('Nieprawidłowa wartość.')

                return errors
            }
        },
        productAmountErrors() {
            const errors = []

            if (!this.$v.product.amount.$dirty) return errors
            !this.$v.product.amount.required && errors.push('Proszę podać nakład.')

            return errors
        },
        productScreenGroupErrors() {
            const errors = []

            if (!this.$v.product.screenGroup.$dirty) return errors
            !this.$v.product.screenGroup.required && errors.push('Proszę wybrać grupę.')

            return errors
        },
        currentProductionFileErrors() {
            if (!this.currentProductionFile) return

            const errors = []

            if (!this.$v.currentProductionFile.$dirty) return errors
            !this.$v.currentProductionFile.maxFileSize && errors.push(`Przekroczono dopuszczalny rozmiar pliku: ${appConfig.orders.productionFileAttachmentSize}MB.`)

            return errors
        },
        productionPropertyValueErrors(componentIndex, propertyIndex) {
            const errors = []

            if (!this.$v.productTemplate.jsonTemplate.campaignProductComponents.$each[componentIndex].campaignComponentProperties.$each[propertyIndex].$dirty) return errors

            !this.$v.productTemplate.jsonTemplate.campaignProductComponents.$each[componentIndex].campaignComponentProperties.$each[propertyIndex].value.required && errors.push('Proszę podać wartość.')

            return errors
        },
        productionProcessOrderErrors(componentIndex, processIndex) {
            const errors = []

            if (!this.$v.productTemplate.jsonTemplate.campaignProductComponents.$each[componentIndex].campaignComponentProcesses.$each[processIndex].$dirty) return errors

            !this.$v.productTemplate.jsonTemplate.campaignProductComponents.$each[componentIndex].campaignComponentProcesses.$each[processIndex].order.required && errors.push('Proszę podać kolejność.')

            return errors
        }
    }
}
</script>
